import Decimal from "decimal.js-light";

// Configure Decimal for ROUND_HALF_UP
Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

/**
 * Strips commas, currency symbols, and trailing zeros from a formatted value
 * while preserving negative signs and allowing up to 4 decimal places
 * @param {string} value - The formatted value to clean
 * @returns {string} - The numeric value as a string without commas,
 *                      currency symbols, or trailing zeros
 */
export const stripPriceFormatting = ({ value }) => {
  if (value == null) {
    return "";
  }

  const inputAsString = typeof value !== "string" ? String(value) : value;
  // First, replace any commas with empty string
  const withoutCommas = inputAsString.replace(/,/g, "");

  // Check if the input has a negative sign
  const isNegative = withoutCommas.trimStart().startsWith("-");

  // Then, remove any non-numeric characters except decimal points and negative signs
  const cleanValue = withoutCommas.replace(/[^0-9.-]/g, "");

  // Handle the case where there could be multiple negative signs or misplaced negative signs
  // Ensure there's only one negative sign at the beginning
  let processedValue = cleanValue;
  if (isNegative) {
    // Remove all negative signs first
    processedValue = processedValue.replace(/-/g, "");
    // Add a single negative sign at the beginning
    processedValue = `-${processedValue}`;
  } else {
    // Remove any negative signs if the original value wasn't negative
    processedValue = processedValue.replace(/-/g, "");
  }

  // IMPORTANT: If the value ends with a decimal point, preserve it
  // This allows users to continue typing after the decimal point
  if (processedValue.endsWith(".")) {
    return processedValue;
  }

  // Split the value into parts before and after the decimal point
  const parts = processedValue.split(".");
  if (parts.length > 1) {
    // If the user is still typing, return as is - don't round yet
    // Only round if we have 5 or more decimal places
    if (parts[1].length > 4) {
      // Round to 4 decimal places using Decimal with ROUND_HALF_UP
      try {
        const decimalValue = new Decimal(processedValue);
        return decimalValue.toFixed(4);
      } catch (_) {
        // Fall back to simpler method if Decimal library fails
        const originalNumber = parseFloat(processedValue);
        const factor = 10000; // For 4 decimal places
        const roundedNumber =
          originalNumber >= 0
            ? Math.round(originalNumber * factor) / factor
            : -Math.round(-originalNumber * factor) / factor;

        return roundedNumber.toString();
      }
    } else {
      // Return as is to preserve user input while typing
      return processedValue;
    }
  }

  // For whole numbers, just return the cleaned value
  return processedValue;
};

/**
 * Formats a price value with commas as thousand separators
 * Always displays at least 2 decimal places, but allows up to 4
 * @param {number|string} price - The price value to format
 * @param {string} currencySymbol - Optional currency symbol (defaults to '

export default formatPrice;)
 * @returns {string} - The formatted price string
 */
export const formatPrice = ({ price, currencySymbol = "$", numberOfDecimals = null }) => {
  if (price == null || price === "" || Number.isNaN(Number(price))) {
    return `${currencySymbol}0.00`;
  }

  const priceStr = price.toString();
  let decimalPlaces = 2; // Minimum 2 decimal places

  // If original input had more than 2 decimal places, respect that up to 4
  if (priceStr.includes(".")) {
    const originalDecimalPlaces = priceStr.split(".")[1].length;
    decimalPlaces = Math.max(2, Math.min(originalDecimalPlaces, 4));
  }

  if (numberOfDecimals !== null && numberOfDecimals !== "" && numberOfDecimals !== undefined) {
    decimalPlaces = numberOfDecimals;
  }

  const numericPrice = typeof price === "string" ? parseFloat(price) : price;
  const isNegative = numericPrice < 0;
  const sign = isNegative ? "-" : "";
  const absolutePrice = isNegative ? Math.abs(numericPrice) : numericPrice;

  // Round to appropriate decimal places using Decimal with ROUND_HALF_UP
  try {
    const decimalValue = new Decimal(absolutePrice);
    const roundedPrice = decimalValue.toFixed(decimalPlaces);

    // Format with commas
    const parts = roundedPrice.split(".");
    const wholePart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    // add a check if parts[1] is undefined for the case where the price is a whole number (no decimal places)
    const formattedPrice = `${wholePart}${parts.length > 1 ? `.${parts[1]}` : ""}`;

    return `${sign}${currencySymbol}${formattedPrice}`;
  } catch (_) {
    // Fall back to native toLocaleString if Decimal fails
    const formattedPrice = absolutePrice.toLocaleString("en-US", {
      minimumFractionDigits:
        numberOfDecimals !== null && numberOfDecimals !== "" && numberOfDecimals !== undefined
          ? numberOfDecimals
          : 2,
      maximumFractionDigits: Math.min(
        Math.max(
          2,
          numberOfDecimals !== null && numberOfDecimals !== "" && numberOfDecimals !== undefined
            ? numberOfDecimals
            : decimalPlaces
        ),
        4
      ),
    });

    return `${sign}${currencySymbol}${formattedPrice}`;
  }
};

export default formatPrice;
